import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Sparkles, Cat } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner } from "@/components/kit";
import { formatDate } from "@/lib/files";

const BENEFITS = {
  pawpal_pro: ["Unlimited AI chat questions", "Photo upload for symptom analysis", "Emergency red-flag detection", "Personalized advice per dog profile"],
  vet_basic: ["Featured badge on your listing", "Higher placement in vet search", "Inbound call analytics"],
  vet_premium: ["Everything in Basic", "Hero-image spotlight card", "Priority in 24/7 Emergency filter", "Dedicated onboarding support"],
  pawpal_pro_bundle: ["Everything in Wooflect Pro (dogs)", "Everything in Purrflect Pro (cats)", "One subscription for your whole household", "Save $4/mo vs. two Pro plans"],
  vet_basic_bundle: ["Featured on Wooflect AND Purrflect", "Reach dog and cat owners", "Save $19/mo vs. buying both"],
  vet_premium_bundle: ["Everything in Basic Bundle", "Premium placement on both apps", "Priority in emergency filters", "Save $99/mo vs. both"],
};

function planLabel(tier) {
  return { pro: "Wooflect Pro", pro_bundle: "Wooflect + Purrflect", basic: "Basic Featured", premium: "Premium Featured" }[tier] || "Free";
}

export default function Subscription() {
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight");
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        apiFetch("/billing/plans", {}, false),
        apiFetch("/billing/status"),
      ]);
      setPlans(p.plans || []);
      setStatus(s);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buy = async (plan) => {
    setBuying(plan.key);
    try {
      const res = await apiFetch("/billing/checkout", { method: "POST", body: JSON.stringify({ plan: plan.key }) });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.message || "Checkout failed");
      setBuying(null);
    }
  };

  const [portalLoading, setPortalLoading] = useState(false);
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await apiFetch("/billing/portal", { method: "POST" });
      window.location.href = res.portal_url;
    } catch (err) {
      toast.error(err.message || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  if (loading) return <FullPageSpinner />;

  const role = status?.role || "user";
  const sub = status?.subscription;
  const soloPlans = plans.filter((p) => p.role_required === role && !p.key.includes("bundle"));
  const bundlePlans = plans.filter((p) => p.role_required === role && p.key.includes("bundle"));

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Subscription" backTo="/app/profile" />

      <div className={`p-5 rounded-3xl mb-6 text-center ${sub?.is_active ? "bg-[#E8F0E5]" : "bg-[#F6DED5]"}`}>
        <div className="flex items-center justify-center gap-2">
          <Sparkles size={22} color={sub?.is_active ? "#6F8F72" : "#E86A45"} />
          <p className="font-display text-xl font-extrabold">
            {sub?.is_active ? `${planLabel(sub.tier)} active` : role === "vet" ? "Free listing" : "Free plan"}
          </p>
        </div>
        {sub?.is_active && sub.expires_at ? (
          <p className="text-sm text-[#4A4746] mt-1">Renews {formatDate(sub.expires_at)}</p>
        ) : role === "user" ? (
          <p className="text-sm text-[#4A4746] mt-1">
            {status?.ai_usage_this_month || 0} of {status?.free_tier_limit || 3} free AI questions used this month
          </p>
        ) : null}
        {sub?.is_active && (
          <button
            data-testid="manage-billing-btn"
            onClick={openPortal}
            disabled={portalLoading}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-[#6F8F72] text-[#4E7150] font-bold text-sm px-5 py-2.5 hover:bg-[#F2EFE9] disabled:opacity-60 transition-colors"
          >
            {portalLoading ? "Opening…" : "Manage billing & cancel"}
          </button>
        )}
      </div>

      <h2 className="font-display text-2xl font-extrabold">{role === "vet" ? "Grow your clinic" : "Upgrade to Pro"}</h2>
      <p className="text-[#4A4746] mb-4">
        {role === "vet"
          ? "Feature your listing at the top of Wooflect and reach more pet parents."
          : "Unlimited AI chat, photo symptom analysis, and emergency detection."}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        {soloPlans.map((p) => (
          <PlanCard key={p.key} p={p} sub={sub} highlight={highlight} buying={buying} onBuy={buy} />
        ))}
      </div>

      {bundlePlans.length > 0 && (
        <div className="mt-8 p-5 rounded-3xl bg-[#F2EFE9]">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-11 w-11 rounded-2xl bg-white flex items-center justify-center shrink-0">
              <Cat size={22} color="#6F8F72" />
            </div>
            <div>
              <p className="font-display text-lg font-extrabold">Wooflect + Purrflect</p>
              <p className="text-sm text-[#4A4746]">Have a cat too? Bundle with our sister app and save vs. two separate plans.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {bundlePlans.map((p) => (
              <PlanCard key={p.key} p={p} sub={sub} highlight={highlight} buying={buying} onBuy={buy} />
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-[#9A938B] text-center mt-6 leading-relaxed">
        Payments processed securely by Stripe. Subscriptions renew monthly and can be canceled anytime.
      </p>
    </div>
  );
}

function PlanCard({ p, sub, highlight, buying, onBuy }) {
  const isCurrent = sub?.is_active && sub.plan === p.key;
  const isHighlight = highlight === p.key;
  return (
    <div
      data-testid={`plan-card-${p.key}`}
      className={`relative p-6 rounded-3xl bg-white border transition-shadow ${
        isCurrent ? "border-[#6F8F72] bg-[#E8F0E5]" : isHighlight ? "border-[#E86A45] border-2 bg-[#FFF7F3] shadow-md" : "border-[#E8E3DA]"
      }`}
    >
      {isHighlight && !isCurrent && (
        <span className="absolute -top-3 right-6 bg-[#E86A45] text-white text-[10px] font-extrabold uppercase tracking-wide px-3 py-1 rounded-full">
          Most popular
        </span>
      )}
      <p className="font-display text-lg font-extrabold">{p.name}</p>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="font-display text-4xl font-black text-[#E86A45]">${p.amount.toFixed(0)}</span>
        <span className="text-[#4A4746]">/month</span>
      </div>
      <p className="text-sm text-[#4A4746] mt-1">{p.description}</p>
      <div className="space-y-2 mt-4">
        {(BENEFITS[p.key] || []).map((b) => (
          <div key={b} className="flex items-start gap-2 text-sm">
            <Check size={16} color="#6F8F72" className="mt-0.5 shrink-0" />
            <span className="text-[#2D2B2A]">{b}</span>
          </div>
        ))}
      </div>
      <button
        data-testid={`plan-buy-${p.key}`}
        disabled={!!buying || isCurrent}
        onClick={() => onBuy(p)}
        className={`w-full mt-5 rounded-full font-extrabold py-3 transition-colors ${
          isCurrent ? "bg-[#E8F0E5] text-[#6F8F72] border-2 border-[#6F8F72]" : "bg-[#E86A45] text-white hover:bg-[#d65f3c] disabled:opacity-60"
        }`}
      >
        {buying === p.key ? "Redirecting…" : isCurrent ? "Current plan" : `Get ${p.name}`}
      </button>
    </div>
  );
}
